import React from "react";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TextField,
    Chip,
    Card,
    CardContent,
    Grid,
    useTheme,
    useMediaQuery,
    InputAdornment,
    IconButton,
} from "@mui/material";

/** Auto-calc shielding effectiveness */
function calcSE(testLevel, reference) {
    const t = parseFloat(testLevel);
    const r = parseFloat(reference);
    if (isNaN(t) || isNaN(r)) return "";
    return String(r - t);
}

function seChipStyle(se, targetAttenuation = 100) {
    const val = parseFloat(se);
    const target = parseFloat(targetAttenuation);
    if (isNaN(val) || isNaN(target)) return { color: "default" };
    if (val >= target) return { color: "success" }; // A
    if (val >= target - 10) return { color: "info" }; // B
    if (val >= target - 20) return { color: "warning" }; // C
    if (val >= target - 30) return { color: "error" }; // D
    return {
        color: "default", // F
        sx: {
            bgcolor: "#6d0000",
            color: "#fff",
            "& .MuiChip-label": { color: "#fff" },
        },
    };
}

function formatFreqLabel(freq) {
    return `${freq.frequency} ${freq.unit || "MHz"}`;
}

export default function ScoreEntry({
    frequencies,
    testPoints,
    scores,
    onChange,
    targetAttenuation,
    onTargetAttenuationChange,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const parsedTarget = parseFloat(targetAttenuation);
    const targetInvalid =
        targetAttenuation === "" ||
        Number.isNaN(parsedTarget) ||
        parsedTarget < 0;

    const handleChange = (freqIdx, point, field, value) => {
        const key = `${freqIdx}-${point}`;
        const existing = scores[key] || {
            testLevel: "",
            shieldingEffectiveness: "",
        };
        const updated = { ...existing, [field]: value };
        if (field === "testLevel") {
            const ref = frequencies[freqIdx]?.reference ?? "";
            updated.shieldingEffectiveness = calcSE(value, ref);
        }
        onChange({ ...scores, [key]: updated });
    };

    // Mobile decimal keypads often omit a minus key, so give users a way to
    // flip the sign without needing to type "-".
    const toggleSign = (freqIdx, point) => {
        const key = `${freqIdx}-${point}`;
        const entry = scores[key] || {
            testLevel: "",
            shieldingEffectiveness: "",
        };
        const value = entry.testLevel;
        const newValue = value.startsWith("-")
            ? value.slice(1)
            : value
              ? `-${value}`
              : "-";
        handleChange(freqIdx, point, "testLevel", newValue);
    };

    if (!frequencies || frequencies.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                Please add frequencies in the previous step first.
            </Typography>
        );
    }

    if (!testPoints || testPoints.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                Please select test points in the previous step first.
            </Typography>
        );
    }

    const targetField = (
        <TextField
            size="small"
            label="Target Attenuation (dB)"
            value={targetAttenuation ?? ""}
            onChange={(e) => onTargetAttenuationChange(e.target.value)}
            error={targetInvalid}
            helperText={
                targetInvalid ? "Enter a number greater than or equal to 0." : " "
            }
            inputProps={{ inputMode: "decimal", min: 0, step: "any" }}
            onKeyDown={(e) => {
                const allowed = [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                    "Home",
                    "End",
                    ".",
                ];
                if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
                    e.preventDefault();
                }
            }}
            sx={{ width: { xs: "100%", sm: 220 } }}
        />
    );

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    mb: 2,
                }}
            >
                {targetField}
                <Typography variant="caption" color="text.secondary">
                    Score colors: green {">="} target, blue {">="} target-10,
                    orange {">="} target-20, red {">="} target-30, maroon below.
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter test levels (dBμV). Shielding Effectiveness is
                auto-calculated: SE = Reference − Test Level.
            </Typography>

            {frequencies.map((freq, freqIdx) => {
                const freqLabel = formatFreqLabel(freq);
                return (
                    <Box key={freqIdx} sx={{ mb: 3 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                                flexWrap: "wrap",
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={700}>
                                {freqLabel}
                            </Typography>
                            {freq.reference && (
                                <Chip
                                    label={`Ref: ${freq.reference} dBμV`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                />
                            )}
                            {freq.antennaLength && (
                                <Chip
                                    label={`Antenna: ${freq.antennaLength}`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        {isMobile ? (
                            <Grid container spacing={1.5}>
                                {testPoints.map((point) => {
                                    const key = `${freqIdx}-${point}`;
                                    const entry = scores[key] || {
                                        testLevel: "",
                                        shieldingEffectiveness: "",
                                    };
                                    const seVal = entry.shieldingEffectiveness;
                                    return (
                                        <Grid item xs={12} key={point}>
                                            <Card
                                                variant="outlined"
                                                sx={{ borderRadius: 1 }}
                                            >
                                                <CardContent
                                                    sx={{
                                                        py: 1.25,
                                                        "&:last-child": {
                                                            pb: 1.25,
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "space-between",
                                                            gap: 1,
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={600}
                                                        >
                                                            {point}
                                                        </Typography>
                                                        {seVal !== "" ? (
                                                            (() => {
                                                                const chip = seChipStyle(
                                                                    seVal,
                                                                    targetAttenuation,
                                                                );
                                                                return (
                                                                    <Chip
                                                                        label={`${seVal} dB`}
                                                                        size="small"
                                                                        color={
                                                                            chip.color
                                                                        }
                                                                        sx={
                                                                            chip.sx
                                                                        }
                                                                    />
                                                                );
                                                            })()
                                                        ) : (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.disabled"
                                                            >
                                                                —
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        label="Test Level (dBμV)"
                                                        value={entry.testLevel}
                                                        onChange={(e) =>
                                                            handleChange(
                                                                freqIdx,
                                                                point,
                                                                "testLevel",
                                                                e.target.value,
                                                            )
                                                        }
                                                        inputProps={{
                                                            inputMode:
                                                                "decimal",
                                                        }}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <IconButton
                                                                        size="small"
                                                                        edge="start"
                                                                        tabIndex={
                                                                            -1
                                                                        }
                                                                        aria-label="Toggle negative"
                                                                        onClick={() =>
                                                                            toggleSign(
                                                                                freqIdx,
                                                                                point,
                                                                            )
                                                                        }
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            fontWeight={700}
                                                                        >
                                                                            ±
                                                                        </Typography>
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        placeholder="e.g. 12"
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 700,
                                                    minWidth: 160,
                                                }}
                                            >
                                                Test Point
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: 700, width: 140 }}
                                            >
                                                Test Level (dBμV)
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: 700, width: 180 }}
                                            >
                                                Shielding Effectiveness
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {testPoints.map((point) => {
                                            const key = `${freqIdx}-${point}`;
                                            const entry = scores[key] || {
                                                testLevel: "",
                                                shieldingEffectiveness: "",
                                            };
                                            const seVal =
                                                entry.shieldingEffectiveness;

                                            return (
                                                <TableRow key={point}>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {point}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={
                                                                entry.testLevel
                                                            }
                                                            onChange={(e) =>
                                                                handleChange(
                                                                    freqIdx,
                                                                    point,
                                                                    "testLevel",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            inputProps={{
                                                                style: {
                                                                    width: 80,
                                                                },
                                                                inputMode:
                                                                    "decimal",
                                                            }}
                                                            placeholder="e.g. 12"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {seVal !== "" ? (
                                                            (() => {
                                                                const chip = seChipStyle(
                                                                    seVal,
                                                                    targetAttenuation,
                                                                );
                                                                return (
                                                                    <Chip
                                                                        label={`${seVal} dB`}
                                                                        size="small"
                                                                        color={
                                                                            chip.color
                                                                        }
                                                                        sx={
                                                                            chip.sx
                                                                        }
                                                                    />
                                                                );
                                                            })()
                                                        ) : (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.disabled"
                                                            >
                                                                —
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
